import { useEffect } from 'react';
import { useObserver } from 'mobx-react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useQuery } from '@apollo/client';

import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useKioskPro } from 'src/hooks/use-kiosk-pro';
import useUser from 'src/hooks/use-user';
import useUI from 'src/hooks/use-ui';
import { useRouter } from 'next/compat/router';
import { useLoyaltyModal } from 'src/components/modals/loyalty';
import { useGetRewards } from 'src/components/loyalty/use-get-rewards';
import { GetDutchiePosLoyaltyConfigDocument } from 'src/gql/graphql';
import { detectLoyaltyIntegration, Integrations } from '../loyalty/use-loyalty';

const PUBLIC_SESSION_PATHS = [
  '/login/public-session-login',
  '/login/public-session-continue',
  '/login/public-session-phone-continue',
];

export const LoyaltyAccountDetection = ({ children }): JSX.Element => {
  const { isEmbedded, isStoreFront, isEmbeddedCarousel } = useUI();
  const isEmbeddedOrStoreFront = (isEmbedded || isStoreFront) && !isEmbeddedCarousel;
  const User = useUser();
  const router = useRouter();
  const isUserLoading = useObserver(() => User.loading);
  const isLoggedIn = useObserver(() => User.isLoggedIn);
  const isUserLoadedAndLoggedIn = !isUserLoading && isLoggedIn;
  const userId = useObserver(() => User.id);
  const phone = useObserver(() => User.phone);
  const birthday = useObserver(() => User.birthday);
  const { dispensary } = useDispensary();
  const loyaltyProgram = detectLoyaltyIntegration(dispensary);
  const dispensaryId = dispensary?.id;
  const pin = User.getLoyaltyPin(dispensaryId);
  const flags = useFlags();
  const isKioskProEnabled = useKioskPro();
  const isShowLoyaltyFlagEnabled = (dispensary?.storeSettings?.showLoyaltyRewards ?? false) || isKioskProEnabled;
  const isShowSpringbigLoyaltyFlagEnabled = flags['growth.ecomm.springbig-member-signup.rollout'] ?? false;
  const isSkipSMSSendingEnabled = flags['pos.customers.skip-sms-sending.rollout'] ?? false;
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- router can be null
  const hasSessionId = Boolean(router?.query?.['session-id']);
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- router can be null
  const isPublicSessionPage = PUBLIC_SESSION_PATHS.some((path) => router?.pathname?.includes(path)) || hasSessionId;
  const showKioskProLoyalty = isKioskProEnabled && !isPublicSessionPage;
  const showSpringbigLoyalty = loyaltyProgram === Integrations.springbig ? isShowSpringbigLoyaltyFlagEnabled : true;
  const isAlpineIQEnabled = loyaltyProgram === Integrations.alpineiq;
  const { open: showLoyaltyModal } = useLoyaltyModal();
  const skip = isAlpineIQEnabled || !phone || !birthday || !dispensaryId || !userId;

  const { data, loading } = useGetRewards({
    phone,
    dispensaryId,
    birthday,
    pin: isSkipSMSSendingEnabled && isAlpineIQEnabled ? 'skip' : pin,
    skip,
    customerId: userId,
  });

  const { data: dposConfigData, loading: dposConfigLoading } = useQuery(GetDutchiePosLoyaltyConfigDocument, {
    variables: {
      dispensaryId,
    },
    skip: loyaltyProgram !== Integrations.dutchiepos,
  });

  useEffect(() => {
    const showForAlpine = loyaltyProgram === Integrations.alpineiq && !pin;
    const showForSpringbig =
      loyaltyProgram === Integrations.springbig && !loading && !data?.getRewardsV2?.userHasWallet;
    const showForDutchiePos =
      loyaltyProgram === Integrations.dutchiepos &&
      !dposConfigLoading &&
      !loading &&
      dposConfigData?.getDutchiePosLoyaltyConfig?.loyaltyRequireOptIn &&
      data?.getRewardsV2?.isLoyaltyMember === false;

    if (
      (isEmbeddedOrStoreFront || showKioskProLoyalty) &&
      isUserLoadedAndLoggedIn &&
      [Integrations.alpineiq, Integrations.springbig, Integrations.dutchiepos].includes(loyaltyProgram) &&
      showSpringbigLoyalty &&
      dispensaryId &&
      !pin &&
      isShowLoyaltyFlagEnabled &&
      phone &&
      birthday &&
      userId &&
      (showForAlpine || showForSpringbig || showForDutchiePos)
    ) {
      void showLoyaltyModal();
    }
  }, [
    dispensaryId,
    loyaltyProgram,
    showSpringbigLoyalty,
    isEmbeddedOrStoreFront,
    isShowLoyaltyFlagEnabled,
    isUserLoadedAndLoggedIn,
    phone,
    birthday,
    userId,
    pin,
    showLoyaltyModal,
    data,
    loading,
    dposConfigData,
    dposConfigLoading,
    showKioskProLoyalty,
  ]);

  return children;
};
